import { useState, forwardRef } from 'react';
import type { CategoryWithResults } from '@/api/generated-catch-backend/model';
import { BiomarkerCard } from '@/app/components/labs/components/biomarker-card/biomarker-card';
import { PortableText } from '@/app/components/questionnaireV2/components/portable-text/portable-text';
import { BiomarkerItemCard } from './biomarker-item-card';
import { BiomarkerFilter } from '../biomarker-filter/biomarker-filter';
import { useBiomarkerFilter } from '../../hooks/use-biomarker-filter';

interface CategorySectionProps {
  category: CategoryWithResults;
}

export const CategorySection = forwardRef<HTMLDivElement, CategorySectionProps>(({ category }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { metadata, counts, biomarkers, summaryContent } = category;

  // Use shared biomarker filtering logic
  const { selectedFilter, setSelectedFilter, availableClassifications, filteredBiomarkers } =
    useBiomarkerFilter(biomarkers);


  return (
    <div ref={ref} className="mb-8 rounded-[10px] bg-[#F7F9FB] px-4 py-5 md:px-12 md:pb-10 md:pt-11">
      {/* Category header */}
      <div className="mb-4">
        <h3 className="mb-2 text-[22px] font-bold leading-[130%] text-catch-dark-blue-90">{metadata.displayName}</h3>
        {summaryContent && summaryContent.length > 0 && (
          <div className="text-[16px] leading-[140%] text-catch-dark-blue-90">
            <PortableText content={summaryContent} />
          </div>
        )}
      </div>

      {/* Aggregate count cards - only show when counts are available (results ready) */}
      {counts && (
        <div className="mb-6 grid grid-cols-3 gap-2 md:gap-6">
          <BiomarkerCard
            count={counts.optimal || 0}
            label="Optimal"
            color="bg-catch-avocado-400"
            totalDots={counts.total || 50}
            variant="light"
            compactDots
          />
          <BiomarkerCard
            count={counts.normal || 0}
            label="Normal"
            color="bg-catch-light-blue"
            totalDots={counts.total || 50}
            variant="light"
            compactDots
          />
          <BiomarkerCard
            count={(counts.outOfRange || 0) + (counts.critical || 0)}
            label="Out of Range"
            color="bg-catch-salmon"
            totalDots={counts.total || 50}
            variant="light"
            compactDots
          />
        </div>
      )}

      {/* See Biomarkers collapsible section */}
      {biomarkers.length > 0 && (
        <div>
          <div className="flex justify-center pt-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full bg-[#183344] px-20 py-2 text-[14px] font-medium leading-[140%] text-white transition-all hover:bg-[#1a3d4f]"
              type="button"
              aria-expanded={isExpanded}
            >
              <span>See Biomarkers</span>
              <svg
                className={`h-6 w-6 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Filter and Biomarker list */}
          {isExpanded && (
            <>
              {/* Filter dropdown - only show if there are filterable biomarkers */}
              {availableClassifications.size > 0 && (
                <div className="mt-6 mb-4">
                  <BiomarkerFilter
                    availableClassifications={availableClassifications}
                    selectedFilter={selectedFilter}
                    onFilterChange={setSelectedFilter}
                  />
                </div>
              )}

              {/* Biomarker list */}
              <div className="mt-3 flex flex-col gap-4">
                {filteredBiomarkers.map((biomarker, index) => (
                  <BiomarkerItemCard key={`${biomarker.metadata.slug}-${index}`} biomarker={biomarker} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
});

CategorySection.displayName = 'CategorySection';
